import { useCallback } from 'react';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import { useMapSettingsFilteringSelector } from '../../../store/mapSettings/filtering/mapSettingsFiltering.selectors';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '../../../store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useClientIdSelector } from '../../../store/selectors/useClientIdSelector';
import { useMapIdSelector } from '../../../store/selectors/useMapIdSelector';
import {
  createDefaultDemographicsMetrics, createDefaultSpreadsheetColumnMetrics,
} from './defaultMetric.factory';
import { getNumericColumns } from './defaultMetric.helpers';

export const useDefaultMetrics = () => {
  const activeGroupColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const activeFiltering = useMapSettingsFilteringSelector();
  const matchupData = useMatchupDataSelector();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const boundaryGroups = useBoundaryGroupsSelector();

  const computeDefaultSpreadsheetColumnMetrics = useCallback(async () => {
    if (!clientId || !mapId) {
      return [];
    }

    const numericColumns = (await getNumericColumns(clientId, mapId, matchupData)).filter(isSuccessBulkSpreadsheet);
    return createDefaultSpreadsheetColumnMetrics(activeGroupColumns, activeFiltering, numericColumns, matchupData);
  }, [clientId, mapId, activeGroupColumns, activeFiltering, matchupData]);

  const computeDefaultDemographicsMetrics = useCallback((boundaryGroupId: BoundaryGroupId) => {
    return createDefaultDemographicsMetrics(boundaryGroups, boundaryGroupId);
  }, [boundaryGroups]);

  const computeDefaultMetrics = useCallback(async (boundaryGroupId: BoundaryGroupId) => {
    return {
      defaultDemographicsMetrics: computeDefaultDemographicsMetrics(boundaryGroupId),
      defaultSpreadsheetColumnMetrics: await computeDefaultSpreadsheetColumnMetrics(),
    };
  }, [computeDefaultDemographicsMetrics, computeDefaultSpreadsheetColumnMetrics]);

  return {
    computeDefaultMetrics,
    computeDefaultDemographicsMetrics,
    computeDefaultSpreadsheetColumnMetrics,
  };
};
